import { useAuth } from '../../../lib/auth';
import AuthLayout from '../../layouts/AuthLayout';
import clsx from 'clsx';
import React, { useState } from 'react';
import { Button } from '../../../components/buttons/Button.tsx';
import { useFormik } from 'formik';
import { cn, transformLaravelToFormikErrors } from '../../../lib/helpers.ts';
import { SchemaDrivenFormFieldProps } from '../../../lib/types.ts';
import {
    useFetchInstitutionLicenses,
    useFetchInstitutionUsers,
} from '../../../lib/fetches.ts';
import {
    UilCheckCircle,
    UilClock,
    UilTimesCircle,
    UilTrashAlt,
} from '@iconscout/react-unicons';
import Alert from '../../../components/Alert.tsx';
import Skeleton from '../../../components/Skeleton.tsx';
import { useNavigate } from 'react-router-dom';
import { renderSchemaForm } from '../../../components/forms/renderSchemaForm.tsx';
import API from '../../../lib/api.ts';
import Badge, { BadgeSkeleton } from '../../../components/badge/Badge.tsx';
import { LicenseBadge } from '../../../components/badge/LicenseBadge.tsx';

function ActiveLicenseSection() {
    const { user, isLoading: isLoadingUser } = useAuth({
        middleware: 'auth',
        redirectTo: '/',
        tmp: 'InstitutionSettingsPage_ActiveLicenseSection',
    });

    const institution = user?.institution;

    const { data: licenses, isLoading } = useFetchInstitutionLicenses();
    const activeLicense = licenses?.active;

    return (
        <div className="rounded-2xl bg-white px-8 py-6 shadow-sm">
            <div className="flex">
                <div className="me-3">
                    {isLoadingUser ? (
                        <div className="flex flex-col space-y-2">
                            <Skeleton className="h-4 w-48 rounded-full" />
                            <Skeleton className="h-4 w-40 rounded-full" />
                            <Skeleton className="h-4 w-56 rounded-full" />
                        </div>
                    ) : (
                        <div className="text-ink-900">
                            <h5>{institution?.name}</h5>
                            <p>{institution?.street}</p>
                            <p>
                                {institution?.postal_code} {institution?.city}
                            </p>
                        </div>
                    )}
                </div>

                <div className="ml-auto flex flex-col items-end justify-start">
                    {isLoading ? (
                        <BadgeSkeleton />
                    ) : (
                        <LicenseBadge
                            license={activeLicense!}
                            className="self-end"
                        />
                    )}
                    {activeLicense?.date_license_ends && (
                        <span className="ms-3 text-sm text-ink-700">
                            platná do:{' '}
                            <span className="text-ink-700">
                                {new Date(
                                    activeLicense?.date_license_ends
                                ).toLocaleDateString('cs')}
                            </span>
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
}

function LicenseHistorySection() {
    const { data: licenses, isLoading } = useFetchInstitutionLicenses();
    const licensesHistory = licenses?.history;

    const navigate = useNavigate();

    return (
        <div className="rounded-2xl bg-white px-8 py-6 shadow-sm">
            <div className="flex items-center justify-between pb-4">
                <h3 className="mb-4">Historie licencí</h3>
                <Button
                    color="product"
                    className="self-end"
                    onClick={() => navigate('/koupit/plna-verze')}
                >
                    {licenses?.active && licenses.active.date_license_ends && new Date(licenses.active.date_license_ends) > new Date() ? 'Prodloužit plnou verzi' : 'Koupit plnou verzi'}
                </Button>
            </div>
            <div className="rounded-t-xl bg-cloud-200 pt-2">
                <table className="w-full table-auto">
                    <thead className="text-left">
                        <tr className="border-b border-cloud-300 bg-cloud-200 font-medium text-ink-800">
                            <th className="p-4 pt-2">Typ</th>
                            <th className="p-4 pt-2 text-right">Platná od</th>
                            <th className="p-4 pt-2 text-right">Platná do</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white">
                        {isLoading && (
                            <tr>
                                <td className="py-4 text-center" colSpan={4}>
                                    <Skeleton
                                        count={4}
                                        className="my-1 h-6 w-full rounded"
                                    />
                                </td>
                            </tr>
                        )}
                        {licensesHistory?.map(
                            ({
                                type_for_human,
                                date_license_starts,
                                date_license_ends,
                            }) => (
                                <tr
                                    key={`${type_for_human}-${date_license_starts}-${date_license_ends}`}
                                    className={cn(
                                        'border-b border-cloud-300 last:border-b-0'
                                    )}
                                >
                                    <td className="px-4 py-3">
                                        {type_for_human}
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        {new Date(
                                            date_license_starts
                                        ).toLocaleDateString('cs')}
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        {new Date(
                                            date_license_ends
                                        ).toLocaleDateString('cs')}
                                    </td>
                                </tr>
                            )
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function NewUserSection() {
    const { mutate } = useFetchInstitutionUsers();
    const [message, setMessage] = useState<string | null>(null);

    const {
        handleSubmit,
        errors,
        touched,
        handleChange,
        values,
        isSubmitting,
        setSubmitting,
        setErrors,
        setValues,
        setFieldValue,
    } = useFormik({
        initialValues: {
            name: '',
            surname: '',
            email: '',
        },
        onSubmit: (values) => {
            setMessage(null);
            API.post('/institution/users', values)
                .then((res) => {
                    setValues({
                        name: '',
                        surname: '',
                        email: '',
                    });
                    mutate();
                    setMessage(res?.data);
                })
                .catch((err) => {
                    const errors = err?.response?.data?.errors;
                    if (errors)
                        setErrors(transformLaravelToFormikErrors(errors));
                })
                .finally(() => {
                    setSubmitting(false);
                });
        },
    });

    const schema: SchemaDrivenFormFieldProps[] = [
        {
            name: 'fullname',
            type: 'grid',
            content: [
                {
                    name: 'name',
                    type: 'text',
                    label: 'Jméno',
                },
                {
                    name: 'surname',
                    type: 'text',
                    label: 'Příjmení',
                },
            ],
        },
        {
            name: 'email',
            type: 'email',
            label: 'E-mail',
        },
    ];

    return (
        <div className="rounded-2xl bg-white px-8 py-6 shadow-sm">
            <div className="mb-4">
                <h3>Nový uživatel</h3>
                <p className="!mt-0 block text-sm text-ink-800">
                    Prosím používejte výhradně emailové adresy pod doménou
                    školy/instituce.
                </p>
            </div>

            {message && <Alert type="success" children={message} />}

            <form className="flex flex-col space-y-6" onSubmit={handleSubmit}>
                {schema.map(
                    renderSchemaForm(
                        values,
                        errors,
                        touched,
                        setFieldValue,
                        setValues,
                        handleChange
                    )
                )}
                <Button
                    type="submit"
                    color="product"
                    className="mt-3 self-end"
                    loadingText="Probíhá změna"
                    isLoading={isSubmitting}
                >
                    Přidat uživatele
                </Button>
            </form>
        </div>
    );
}

function InstitutionUserApprovalButton({
    userId,
    approval,
}: {
    userId: number;
    approval: boolean;
}) {
    const { mutate } = useFetchInstitutionUsers();

    const [isSubmitting, setIsSubmitting] = React.useState(false);

    return (
        <Button
            color={approval ? 'green' : 'red'}
            title={approval ? 'Schválit' : 'Odmítnout'}
            className="p-2"
            onClick={() => {
                setIsSubmitting(true);
                API.post(`/institution/users/${userId}`, {
                    approval: approval,
                })
                    .then(() => {
                        mutate();
                    })
                    .finally(() => {
                        setIsSubmitting(false);
                    });
            }}
            isLoading={isSubmitting}
        >
            {approval ? (
                <UilCheckCircle size={20} />
            ) : (
                <UilTimesCircle size={20} />
            )}
        </Button>
    );
}

function DeleteInstitutionUserButton({ userId }: { userId: number }) {
    const { mutate } = useFetchInstitutionUsers();

    const [isSubmitting, setIsSubmitting] = React.useState(false);

    return (
        <Button
            color="red"
            className="p-2"
            onClick={() => {
                setIsSubmitting(true);
                API.delete(`/institution/users/${userId}`)
                    .then(() => {
                        mutate();
                    })
                    .finally(() => {
                        setIsSubmitting(false);
                    });
            }}
            isLoading={isSubmitting}
        >
            <UilTrashAlt size={20} />
        </Button>
    );
}

function CurrentUsersSection() {
    const { data: users, isLoading } = useFetchInstitutionUsers();

    const { user } = useAuth({
        middleware: 'auth',
        redirectTo: '/',
        tmp: 'InstitutionSettingsPage_CurrentUsersSection',
    });

    // Only for contact person
    const navigate = useNavigate();
    if (!user?.is_institution_contact_person) navigate('/');

    return (
        <div className="mt-8 rounded-2xl bg-white px-8 py-6 shadow-sm">
            <h3 className="mb-4">Uživatelé v instituci</h3>
            <div className="rounded-t-xl bg-cloud-200 pt-2">
                <table className="w-full table-auto">
                    <thead className="text-left">
                        <tr className="border-b border-cloud-300 bg-cloud-200 font-medium text-ink-800">
                            <th className="p-4 pt-2">Jméno</th>
                            <th className="p-4 pt-2">E-mail</th>
                            <th className="p-4 pt-2 text-center">Stav</th>
                            <th className="p-4 pt-2"></th>
                        </tr>
                    </thead>
                    <tbody className="bg-white">
                        {isLoading && (
                            <tr>
                                <td className="py-4 text-center" colSpan={4}>
                                    <Skeleton
                                        count={4}
                                        className="my-1 h-6 w-full rounded"
                                    />
                                </td>
                            </tr>
                        )}
                        {users?.map(
                            ({
                                id,
                                full_name,
                                email,
                                deleted_at,
                                approval_code,
                            }) => (
                                <tr
                                    key={id}
                                    className={cn(
                                        'border-b border-cloud-300 last:border-b-0',
                                        'hover:bg-cloud-100',
                                        deleted_at !== null && 'text-ink-800'
                                    )}
                                >
                                    <td className="px-4 py-3">{full_name}</td>
                                    <td className="px-4 py-3">{email}</td>
                                    <td className="px-4 py-3 text-center">
                                        {deleted_at === null ? (
                                            <div className="flex flex-col items-center justify-center space-y-0.5">
                                                <UilCheckCircle
                                                    size={26}
                                                    className="mx-auto shrink-0 text-green-500"
                                                />
                                                <span className="text-xs text-green-500">
                                                    Aktivní
                                                </span>
                                            </div>
                                        ) : (
                                            <div className="flex flex-col items-center justify-center space-y-0.5">
                                                <UilClock
                                                    size={26}
                                                    className="mx-auto shrink-0 text-orange-500"
                                                />
                                                <span className="text-xs text-orange-500">
                                                    Čeká na schválení
                                                </span>
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-4 py-3">
                                        {deleted_at !== null &&
                                            approval_code !== null && (
                                                <>
                                                    <InstitutionUserApprovalButton
                                                        userId={id}
                                                        approval={true}
                                                    />
                                                    <InstitutionUserApprovalButton
                                                        userId={id}
                                                        approval={false}
                                                    />
                                                </>
                                            )}
                                        {id !== user?.id && (
                                            <DeleteInstitutionUserButton
                                                userId={id}
                                            />
                                        )}
                                    </td>
                                </tr>
                            )
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default function InstitutionSettingsPage() {
    useAuth({
        middleware: 'auth',
        redirectTo: '/',
        tmp: 'InstitutionSettingsPage',
    });

    return (
        <AuthLayout>
            <main
                className={clsx(
                    'relative',
                    'mx-auto box-content max-w-2xl py-9',
                    'px-4 sm:px-8 md:px-10'
                )}
            >
                <div className="mb-14">
                    <h1>Nastavení instituce</h1>

                    <ActiveLicenseSection />
                </div>

                <div className="mb-14">
                    <h2>Uživatelé</h2>
                    <NewUserSection />

                    <CurrentUsersSection />
                </div>

                <div>
                    <h2>Licence</h2>
                    <LicenseHistorySection />
                </div>
            </main>
        </AuthLayout>
    );
}
